import { ArrowLeft, Building, TrendingUp, Calendar, Shield, Award, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useInvestments } from "@/hooks/useInvestments";

const OffPlanInvestmentsPage = () => {
  const { data: investments, isLoading } = useInvestments();

  const benefits = [
    { icon: TrendingUp, title: "High ROI", description: "Average 18-22% annual returns" },
    { icon: Calendar, title: "Flexible Payments", description: "Milestone-based payment plans" },
    { icon: Shield, title: "Secure Investment", description: "Legal protection and escrow accounts" },
    { icon: Award, title: "Early Bird Pricing", description: "Below-market rates before completion" },
  ];

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        <div className="bg-gradient-to-br from-primary to-primary/95 py-20 md:py-32">
          <div className="px-4 md:px-16 max-w-7xl mx-auto text-center">
            <div className="mb-6">
              <a href="/" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors">
                <ArrowLeft className="w-4 h-4" /><span className="font-body text-sm">Back to Home</span>
              </a>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-light text-primary-foreground mb-8">Off-Plan Investments</h1>
            <div className="max-w-3xl mx-auto">
              <p className="font-body text-lg md:text-xl leading-relaxed text-primary-foreground/80">
                Secure your future with our carefully selected off-plan projects.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-card/95 border border-border/20 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4"><b.icon className="w-6 h-6 text-accent" /></div>
                <h4 className="font-display text-lg font-light text-foreground mb-2">{b.title}</h4>
                <p className="font-body text-xs text-muted-foreground">{b.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 md:px-16 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">Current Opportunities</h2>
            </div>
            {isLoading ? (
              <p className="text-center text-primary-foreground/70 font-body">Loading investments...</p>
            ) : investments && investments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {investments.map((inv) => (
                  <div key={inv.id} className="group bg-card/95 border border-border/20 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={inv.image} alt={inv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/20 to-transparent" />
                      <div className="absolute top-4 right-4"><div className="px-3 py-1 bg-accent/90 rounded-full"><span className="font-body text-xs text-accent-foreground font-medium">{inv.completion}</span></div></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="font-display text-xl font-light text-primary-foreground mb-2">{inv.title}</h3>
                        <div className="flex items-center gap-3 text-primary-foreground/80 text-sm"><span>{inv.location}</span><span>•</span><span>{inv.type}</span></div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-2xl font-display font-bold text-accent">{inv.price}</div>
                        <div className="text-right">
                          <div className="text-lg font-display font-bold text-accent">{inv.roi}</div>
                          <div className="font-body text-xs text-muted-foreground">Expected ROI</div>
                        </div>
                      </div>
                      <p className="font-body text-sm text-muted-foreground mb-4">{inv.description}</p>
                      <div className="space-y-2 mb-4">
                        {inv.features.slice(0, 3).map((f, idx) => (
                          <div key={idx} className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-accent rounded-full" /><span className="font-body text-xs text-foreground">{f}</span></div>
                        ))}
                      </div>
                      <a href="/contact" className="inline-flex items-center gap-2 font-body text-sm text-accent hover:text-accent/80 transition-colors">Get Details <Building className="w-4 h-4" /></a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-primary-foreground/70 font-body">No investment opportunities available yet.</p>
            )}
          </div>
        </div>

        <div className="px-4 md:px-16 py-16">
          <div className="text-center">
            <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-6">Start Your Investment Journey</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-body text-sm tracking-wider uppercase py-3 px-8 rounded-lg hover:bg-accent/90 transition-colors">
                <Phone className="w-4 h-4" /> Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OffPlanInvestmentsPage;
